import React, { useState, useEffect } from 'react';
import { Input } from '../components/ui/input';
import { Mail, Linkedin, Search, Filter, ChevronRight, MapPin, Briefcase, Award, GraduationCap, X, Users, ArrowRight, User } from 'lucide-react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';
import { 
  PageHeader, 
  Section, 
  Container, 
  SectionHeader,
  FadeInOnScroll,
  PrimaryButton
} from '../components/DesignSystem';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Team Listing Page
const TeamList = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDiscipline, setSelectedDiscipline] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [filters, setFilters] = useState({ disciplines: [], locations: [] });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team`);
      setTeamMembers(response.data.team || []);
      setFilters(response.data.filters || { disciplines: [], locations: [] });
    } catch (error) {
      console.error('Error fetching team:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter team members
  const filteredMembers = teamMembers.filter(member => {
    const matchesSearch = !searchQuery || 
      member.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.position?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.discipline?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesDiscipline = !selectedDiscipline || member.discipline === selectedDiscipline;
    const matchesLocation = !selectedLocation || member.officeLocation === selectedLocation;
    
    return matchesSearch && matchesDiscipline && matchesLocation;
  });

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedDiscipline('');
    setSelectedLocation('');
  };

  const hasActiveFilters = searchQuery || selectedDiscipline || selectedLocation;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-6 text-gray-600 font-medium text-lg">Loading team...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="team-page">
      <SEO title="Meet the Team" description="Meet our dedicated team of MEP engineering professionals at FURD Ltd." />
      
      {/* Hero Section */}
      <PageHeader
        badge="Our People"
        badgeIcon={Users}
        title="Meet the Team"
        subtitle="Our dedicated team of MEP engineering professionals committed to delivering excellence in every project."
      />

      {/* Search & Filter Section */}
      <Section background="white" padding="sm">
        <Container>
          <div className="flex flex-col md:flex-row gap-4 items-center py-4 border-b border-gray-100">
            {/* Search */}
            <div className="relative flex-1 w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, role, or discipline..."
                className="pl-12 h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                data-testid="team-search-input"
              />
            </div>
            
            {/* Filter Toggle (Mobile) */}
            <button 
              className="md:hidden w-full flex items-center justify-center gap-2 px-6 py-3 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4" /> Filters
            </button>

            {/* Desktop Filters */}
            <div className="hidden md:flex gap-3">
              {filters.disciplines.length > 0 && (
                <select
                  value={selectedDiscipline}
                  onChange={(e) => setSelectedDiscipline(e.target.value)}
                  className="h-12 border border-gray-200 rounded-xl px-4 text-sm bg-white hover:border-blue-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  data-testid="discipline-filter"
                >
                  <option value="">All Disciplines</option>
                  {filters.disciplines.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              )}
              
              {filters.locations.length > 0 && (
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="h-12 border border-gray-200 rounded-xl px-4 text-sm bg-white hover:border-blue-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  data-testid="location-filter"
                >
                  <option value="">All Locations</option>
                  {filters.locations.map(l => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
              )}
              
              {hasActiveFilters && (
                <button 
                  onClick={clearFilters} 
                  className="flex items-center gap-2 px-4 text-gray-500 hover:text-red-500 transition-colors"
                >
                  <X className="w-4 h-4" /> Clear
                </button>
              )}
            </div>
          </div>

          {/* Mobile Filters Dropdown */}
          {showFilters && (
            <FadeInOnScroll>
              <div className="md:hidden mt-4 p-6 bg-gray-50 rounded-2xl space-y-4">
                {filters.disciplines.length > 0 && (
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-2 block">Discipline</label>
                    <select
                      value={selectedDiscipline}
                      onChange={(e) => setSelectedDiscipline(e.target.value)}
                      className="w-full h-12 border border-gray-200 rounded-xl px-4 text-sm bg-white"
                    >
                      <option value="">All Disciplines</option>
                      {filters.disciplines.map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                {filters.locations.length > 0 && (
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-2 block">Location</label>
                    <select
                      value={selectedLocation}
                      onChange={(e) => setSelectedLocation(e.target.value)}
                      className="w-full h-12 border border-gray-200 rounded-xl px-4 text-sm bg-white"
                    >
                      <option value="">All Locations</option>
                      {filters.locations.map(l => (
                        <option key={l} value={l}>{l}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                {hasActiveFilters && (
                  <button 
                    onClick={clearFilters} 
                    className="w-full py-3 border border-gray-200 rounded-xl text-gray-600 hover:bg-white transition-colors"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </FadeInOnScroll>
          )}
        </Container>
      </Section>

      {/* Results Count */}
      {hasActiveFilters && (
        <Section background="white" padding="none">
          <Container>
            <FadeInOnScroll>
              <p className="py-4 text-gray-500">
                Showing <span className="font-bold text-gray-900">{filteredMembers.length}</span> of {teamMembers.length} team members
              </p>
            </FadeInOnScroll>
          </Container>
        </Section>
      )}

      {/* Team Grid */}
      <Section background="light" padding="default">
        <Container>
          {filteredMembers.length === 0 ? (
            <FadeInOnScroll>
              <div className="text-center py-20">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="w-10 h-10 text-gray-400" />
                </div>
                <p className="text-gray-500 text-xl font-medium mb-4">No team members found matching your criteria.</p>
                <button 
                  onClick={clearFilters}
                  className="text-blue-600 font-semibold hover:underline"
                >
                  Clear all filters
                </button>
              </div>
            </FadeInOnScroll>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredMembers.map((member, index) => {
                // Use slug, then id, then _id for routing
                const memberSlug = member.slug || member.id || member._id;
                // Get short bio or truncate full bio
                const shortBioText = member.shortBio || member.fullBio || member.bio || '';
                const displayBio = shortBioText.length > 120 ? shortBioText.substring(0, 120) + '...' : shortBioText;
                
                return (
                  <FadeInOnScroll key={member._id || member.id || index} delay={index * 50}>
                    <Link
                      to={`/team/${memberSlug}`}
                      className="group block bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 border border-gray-100/80 hover:border-blue-200/50 transform hover:-translate-y-2 h-full flex flex-col"
                      data-testid={`team-member-${memberSlug}`}
                    >
                      {/* Photo */}
                      <div className="h-72 overflow-hidden relative bg-gray-100 flex-shrink-0">
                        {(member.photo || member.image) ? (
                          <img 
                            src={member.photo || member.image} 
                            alt={member.name}
                            className="w-full h-full object-cover"
                            style={{ objectPosition: `center ${member.photoPosition ?? 20}%` }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-500 rounded-full flex items-center justify-center">
                              <User className="w-10 h-10 text-white" />
                            </div>
                          </div>
                        )}
                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-blue-600/90 via-blue-600/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-end p-5">
                          <span className="text-white font-semibold flex items-center gap-2 text-sm">
                            View Profile <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </span>
                        </div>
                      </div>
                      
                      {/* Info */}
                      <div className="p-5 flex-1 flex flex-col">
                        <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">{member.name}</h3>
                        <p className="text-blue-600 font-medium text-sm mb-2">{member.position}</p>
                        
                        {/* Short Bio Preview - Fixed height with line clamp */}
                        {displayBio && (
                          <p className="text-gray-600 text-sm leading-relaxed mb-3 line-clamp-3 flex-1">
                            {displayBio}
                          </p>
                        )}
                        
                        {/* Tags */}
                        <div className="flex flex-wrap gap-2 mt-auto pt-2">
                          {member.discipline && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full font-medium">
                              {member.discipline}
                            </span>
                          )}
                          {member.officeLocation && (
                            <span className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full flex items-center gap-1 font-medium">
                              <MapPin className="w-3 h-3" /> {member.officeLocation}
                            </span>
                          )}
                        </div>
                        
                        {/* View Profile Button */}
                        <div className="mt-4 pt-3 border-t border-gray-100">
                          <span className="text-blue-600 font-semibold text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                            View Profile <ArrowRight className="w-4 h-4" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </FadeInOnScroll>
                );
              })}
            </div>
          )}
        </Container>
      </Section>

      {/* CTA Section */}
      <Section background="dark" padding="default">
        <Container className="text-center relative z-10">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-[200px] -right-[200px] w-[500px] h-[500px] bg-blue-500/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-[150px] -left-[150px] w-[400px] h-[400px] bg-blue-400/10 rounded-full blur-3xl" />
          </div>
          
          <FadeInOnScroll>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">Join Our Team</h2>
            <p className="text-lg md:text-xl text-blue-100/80 mb-10 max-w-2xl mx-auto leading-relaxed">
              We&apos;re always looking for talented engineers to join our growing team.
            </p>
            <PrimaryButton to="/contact" size="lg" icon={ArrowRight}>
              Get in Touch
            </PrimaryButton>
          </FadeInOnScroll>
        </Container>
      </Section>
    </div>
  );
};

// Team Profile Page
const TeamProfile = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMember();
  }, [memberId]);

  const fetchMember = async () => {
    try {
      const response = await axios.get(`${API}/team/${memberId}`);
      // Backend returns {member: {...}} 
      setMember(response.data.member || response.data);
    } catch (error) {
      console.error('Error fetching team member:', error);
      navigate('/team');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-6 text-gray-600 font-medium text-lg">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 text-lg mb-4">Team member not found.</p>
          <Link to="/team" className="text-blue-600 font-semibold hover:underline">
            Back to Team
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="team-profile-page">
      <SEO 
        title={`${member.name} - ${member.position}`} 
        description={member.bio || `Meet ${member.name}, ${member.position} at FURD Ltd.`} 
      />

      {/* Hero Section */}
      <section className="relative py-20 md:py-24 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-[200px] -right-[200px] w-[500px] h-[500px] bg-blue-500/15 rounded-full blur-3xl" />
          <div className="absolute -bottom-[150px] -left-[150px] w-[400px] h-[400px] bg-blue-400/10 rounded-full blur-3xl" />
        </div>
        
        <Container className="relative z-10">
          {/* Breadcrumb */}
          <FadeInOnScroll>
            <div className="flex items-center gap-2 text-sm text-blue-200 mb-8">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/team" className="hover:text-white transition-colors">Team</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white font-medium">{member.name}</span>
            </div>
          </FadeInOnScroll>
          
          <div className="grid md:grid-cols-3 gap-10 items-start">
            {/* Photo & Contact */}
            <FadeInOnScroll direction="left">
              <div className="text-center md:text-left">
                <div className="relative inline-block mb-6">
                  <div className="absolute -inset-4 bg-gradient-to-br from-blue-500/30 to-blue-400/20 rounded-3xl blur-xl" />
                  {(member.photo || member.image) ? (
                    <img 
                      src={member.photo || member.image} 
                      alt={member.name}
                      className="relative w-48 h-48 rounded-3xl object-cover shadow-2xl border-4 border-white/20"
                      style={{ objectPosition: `center ${member.photoPosition ?? 20}%` }}
                    />
                  ) : (
                    <div className="relative w-48 h-48 rounded-3xl bg-gradient-to-br from-blue-600 to-blue-500 flex items-center justify-center shadow-2xl">
                      <User className="w-24 h-24 text-white" />
                    </div>
                  )}
                </div>
                
                {/* Contact Links */}
                <div className="space-y-3">
                  {member.email && (
                    <a 
                      href={`mailto:${member.email}`}
                      className="flex items-center gap-3 p-3 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-colors group"
                    >
                      <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center group-hover:bg-blue-500 transition-colors">
                        <Mail className="w-5 h-5 text-blue-300 group-hover:text-white" />
                      </div>
                      <div className="text-left">
                        <div className="text-sm font-medium text-white">Email</div>
                        <div className="text-sm text-blue-200 truncate max-w-[160px]">{member.email}</div>
                      </div>
                    </a>
                  )}
                  {member.linkedin && (
                    <a 
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-colors group"
                    >
                      <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center group-hover:bg-blue-500 transition-colors">
                        <Linkedin className="w-5 h-5 text-blue-300 group-hover:text-white" />
                      </div>
                      <div className="text-left">
                        <div className="text-sm font-medium text-white">LinkedIn</div>
                        <div className="text-sm text-blue-200">View Profile</div>
                      </div>
                    </a>
                  )}
                </div>
              </div>
            </FadeInOnScroll>
            
            {/* Info */}
            <FadeInOnScroll direction="right" delay={150} className="md:col-span-2">
              <div className="flex flex-wrap gap-2 mb-6">
                {member.discipline && (
                  <span className="text-sm bg-blue-500/20 backdrop-blur-sm text-blue-200 px-4 py-2 rounded-full border border-blue-400/30 font-medium">
                    {member.discipline}
                  </span>
                )}
                {member.officeLocation && (
                  <span className="text-sm bg-white/10 backdrop-blur-sm text-blue-100 px-4 py-2 rounded-full flex items-center gap-2 font-medium">
                    <MapPin className="w-4 h-4" /> {member.officeLocation}
                  </span>
                )}
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-3 tracking-tight">{member.name}</h1>
              <p className="text-xl md:text-2xl text-blue-300 font-semibold mb-8">{member.position}</p>
              
              {/* Full Bio */}
              {(member.fullBio || member.bio) && (
                <div className="prose prose-lg max-w-none">
                  <p className="text-blue-100/80 leading-relaxed whitespace-pre-wrap text-lg">{member.fullBio || member.bio}</p>
                </div>
              )}
            </FadeInOnScroll>
          </div>
        </Container>
      </section>

      {/* Details Sections */}
      <Section background="light" padding="default">
        <Container>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Key Experience */}
            {member.keyExperience && member.keyExperience.length > 0 && (
              <FadeInOnScroll delay={100}>
                <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 hover:shadow-xl transition-shadow h-full">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-400 rounded-xl flex items-center justify-center shadow-lg">
                      <Briefcase className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">Key Experience</h2>
                  </div>
                  <ul className="space-y-4">
                    {member.keyExperience.map((exp, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-600 leading-relaxed">{exp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeInOnScroll>
            )}

            {/* Qualifications */}
            {member.qualifications && member.qualifications.length > 0 && (
              <FadeInOnScroll delay={200}>
                <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 hover:shadow-xl transition-shadow h-full">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-400 rounded-xl flex items-center justify-center shadow-lg">
                      <GraduationCap className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">Qualifications</h2>
                  </div>
                  <ul className="space-y-4">
                    {member.qualifications.map((qual, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-600 leading-relaxed">{qual}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeInOnScroll>
            )}

            {/* Professional Memberships */}
            {member.memberships && member.memberships.length > 0 && (
              <FadeInOnScroll delay={300}>
                <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 hover:shadow-xl transition-shadow h-full">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-400 rounded-xl flex items-center justify-center shadow-lg">
                      <Award className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">Professional Memberships</h2>
                  </div>
                  <ul className="space-y-4">
                    {member.memberships.map((mem, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-600 leading-relaxed">{mem}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeInOnScroll>
            )}

            {/* Projects Worked On */}
            {member.projectsWorkedOn && member.projectsWorkedOn.length > 0 && (
              <FadeInOnScroll delay={400}>
                <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 hover:shadow-xl transition-shadow h-full">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-400 rounded-xl flex items-center justify-center shadow-lg">
                      <Briefcase className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">Notable Projects</h2>
                  </div>
                  <ul className="space-y-4">
                    {member.projectsWorkedOn.map((proj, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-600 leading-relaxed">{proj}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeInOnScroll>
            )}
          </div>
        </Container>
      </Section>

      {/* CTA */}
      <Section background="dark" padding="default">
        <Container className="text-center relative z-10">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-[200px] -right-[200px] w-[500px] h-[500px] bg-blue-500/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-[150px] -left-[150px] w-[400px] h-[400px] bg-blue-400/10 rounded-full blur-3xl" />
          </div>
          
          <FadeInOnScroll>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">Work with Our Team</h2>
            <p className="text-lg md:text-xl text-blue-100/80 mb-10 max-w-2xl mx-auto leading-relaxed">
              Get in touch to discuss how our experienced team can help with your next project.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <PrimaryButton to="/contact" size="lg" icon={ArrowRight}>
                Contact Us
              </PrimaryButton>
              <Link 
                to="/team" 
                className="inline-flex items-center justify-center gap-2 px-8 py-4 text-lg bg-white/10 border border-white/30 hover:bg-white/20 text-white font-semibold rounded-xl transition-all duration-300"
              >
                View All Team
              </Link>
            </div>
          </FadeInOnScroll>
        </Container>
      </Section>
    </div>
  );
};

// Export both components
export { TeamList, TeamProfile };
export default TeamList;
